/**
 * Punch List — service layer (Construction Gates Phase D core). Backed by api/punch-items.js
 * (Supabase `punch_lists` + `punch_items` + `punch_item_events` + the public `punch-photos`
 * bucket). One punch list per PROJECT, created lazily on first open.
 *
 * Item ids are CLIENT-GENERATED uuids (minted at capture — the field-observation contract), so
 * the /field offline queue replays through these exact functions (field/offline.js).
 *
 * Status workflow: open → in_progress → ready_for_review → verified → closed. This service is
 * the TEAM side; the GC portal's ready-for-review flag goes through the portal token path
 * (api/contractor-submit.js — Phase D trimmings), never through here.
 */

export const PUNCH_STATUSES = ['open', 'in_progress', 'ready_for_review', 'verified', 'closed']
export const PUNCH_STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  ready_for_review: 'Ready for Review',
  verified: 'Verified',
  closed: 'Closed',
}

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/punch-items' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Punch list request failed (' + res.status + ')'))
  return data
}

/** Get-or-create the project's punch list + its items (each item carries its event trail). */
export function getOrCreatePunchList(projectId) {
  return call('GET', null, 'projectId=' + encodeURIComponent(projectId))
}

/** Upload a photo File and return its stored path (append to an item's photoPaths). */
export async function uploadPunchPhoto(punchListId, file) {
  const { signedUrl, path } = await call('POST', { action: 'photoUploadUrl', punchListId, filename: file.name })
  const res = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' }, body: file })
  if (!res.ok) throw new Error('Photo upload failed (' + res.status + ')')
  return path
}

/** Create or update a punch item (idempotent on item.id — a stable client-generated uuid). */
export async function upsertPunchItem(punchListId, item, byName) {
  const { item: saved } = await call('POST', { action: 'upsertItem', punchListId, item, byName })
  return saved
}

/** Team-side status move (any transition; every move lands in the item's event trail). */
export async function setPunchItemStatus(itemId, status, byName) {
  const { item } = await call('POST', { action: 'setStatus', itemId, status, byName })
  return item
}

export function deletePunchItem(itemId) {
  return call('POST', { action: 'deleteItem', itemId })
}

/** Set the punch list's intended walk order — an ordered array of room_key values ('room:<id>' |
 *  'site:<slug>'), decided ahead of a site visit (like organizing a shopping list by aisle). */
export async function setPunchRoomOrder(punchListId, order) {
  const { list } = await call('POST', { action: 'setRoomOrder', punchListId, order })
  return list
}

/** Hide fixed site categories ('site:<slug>') that don't apply to this project's lot (e.g. no
 *  rear yard on an attached lot). `disabled` is the array of category keys to hide. */
export async function setPunchDisabledSiteCategories(punchListId, disabled) {
  const { list } = await call('POST', { action: 'setDisabledSiteCategories', punchListId, disabled })
  return list
}

/** Persist the Contractor name shown on the compiled PDF's issuance metadata line — a plain text
 *  field, confirmed/edited in the export dialog every time so it never goes stale. */
export async function setPunchContractorName(punchListId, contractorName) {
  const { list } = await call('POST', { action: 'setContractorName', punchListId, contractorName })
  return list
}

/** Open the compiled punch-list PDF (letterhead + per-row QR into the contractor portal) in a new
 *  tab — on-demand preview, independent of what's frozen to Documents. contractorName (from the
 *  export dialog) stamps the PDF's "Contractor:" line for this render.
 *
 *  A large punch list (many rows/photos) can take a while to compile server-side, during which
 *  nothing else on screen changes — pass an already-opened `tab` (see PunchListPdfHost.jsx, which
 *  opens it synchronously on click and shows a "Generating…" placeholder) and this navigates that
 *  same tab to the finished PDF instead of opening a brand new one once it's ready. Falls back to
 *  opening a fresh tab if none is passed, preserving the old standalone behavior. */
export async function previewPunchListPdf(projectId, msName, contractorName, tab) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/punch-items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'pdf', projectId, msName, contractorName }),
  })
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || ('Punch list PDF failed (' + res.status + ')')) }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  if (tab && !tab.closed) tab.location = url
  else window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

/** Compile the punch list PDF and freeze it into the given milestone's Documents (source
 *  'punch-list') — replaces any prior punch-list doc. Returns { ok, document }. contractorName
 *  (from the export dialog) stamps the PDF's "Contractor:" line for this render. */
export function compilePunchListPdf(projectId, milestoneId, msName, contractorName) {
  return call('POST', { action: 'compilePdf', projectId, milestoneId, msName, contractorName })
}
